import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { MyAdsPage } from '@/ads/MyAdsPage';
import { NewAdPage } from '@/ads/NewAdPage';
import { AppShell } from '@/components/AppShell';
import { NotFoundPage } from '@/components/NotFoundPage';
import { LoginPage } from '@/auth/LoginPage';
import { ForgotPasswordPage, PasswordResetPage } from '@/auth/PasswordResetPage';
import { RequireAuth } from '@/auth/RequireAuth';
import { DEFAULT_FEED, FeedPage } from '@/feed/FeedPage';
import { ListingDetailPage } from '@/feed/ListingDetailPage';
import { MyOrganizationPage } from '@/organization/MyOrganizationPage';
import { EditListingPage } from '@/post/EditListingPage';
import { MyListingsPage } from '@/post/MyListingsPage';
import { PostPage } from '@/post/PostPage';

/**
 * Настройки TanStack Query — те же, что в Light: 4xx не ретраим (бизнес-ошибки),
 * фокус окна не рефетчит. Фабрика экспортируется, чтобы тесты поднимали
 * изолированный клиент без ретраев.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error: unknown) => {
          if (error && typeof error === 'object' && 'status' in error) {
            const status = (error as { status: number }).status;
            if (status >= 400 && status < 500) return false;
          }
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}

const queryClient = createQueryClient();

/** Дерево маршрутов без роутера — переиспользуется тестами поверх MemoryRouter. */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to={`/feed/${DEFAULT_FEED}`} replace />} />
        <Route path="/feed/:feed" element={<FeedPage />} />
        <Route path="/listings/:id" element={<ListingDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/my/organization" element={<MyOrganizationPage />} />
          <Route path="/my/listings" element={<MyListingsPage />} />
          <Route path="/my/listings/:id/edit" element={<EditListingPage />} />
          <Route path="/post" element={<PostPage />} />
          <Route path="/ads/new" element={<NewAdPage />} />
          <Route path="/my/ads" element={<MyAdsPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
